import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Users, TrendingUp, DollarSign, Award, AlertTriangle,
  Download, Filter, Search, ChevronDown, Check, X,
  Calendar as CalendarIcon, Phone, Mail, Briefcase, BarChart3, Clock,
  AlertCircle, CheckCircle2, User, FileText, ArrowUpRight,
  RotateCcw, CalendarRange
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Label } from '../ui/label';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  'Pending': 'bg-slate-100 text-slate-700',
  'Shortlisted': 'bg-blue-100 text-blue-700',
  'Initial Cleared': 'bg-blue-100 text-blue-700',
  'Interviewed': 'bg-amber-100 text-amber-700',
  'Registered': 'bg-amber-100 text-amber-700',
  'Hired': 'bg-emerald-100 text-emerald-700',
  'Placed': 'bg-emerald-100 text-emerald-700',
  'Rejected': 'bg-red-100 text-red-700',
};

const PROCESS_CHARGE_OPTIONS = [
  { label: '₹10,000 - ₹12,000', min: 10000, max: 12000, incentive: 1200 },
  { label: '₹20,000 - ₹25,000', min: 20000, max: 25000, incentive: 2000 },
  { label: '₹26,000 - ₹30,000', min: 26000, max: 30000, incentive: 2500 },
  { label: '₹35,000 - ₹40,000', min: 35000, max: 40000, incentive: 3000 },
  { label: '₹45,000 - ₹50,000', min: 45000, max: 50000, incentive: 4000 },
  { label: '₹50,000 - ₹60,000', min: 50000, max: 60000, incentive: 5000 },
];

export default function PartnerDashboard() {
  const [activeView, setActiveView] = useState('overview');
  const [stats, setStats] = useState(null);
  const [partners, setPartners] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [monthlyPerformance, setMonthlyPerformance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [partnerFilter, setPartnerFilter] = useState('all');
  
  // Performance filter states
  const currentDate = new Date();
  const [perfMonthFilter, setPerfMonthFilter] = useState((currentDate.getMonth() + 1).toString());
  const [perfYearFilter, setPerfYearFilter] = useState(currentDate.getFullYear().toString());
  const [perfDaysFilter, setPerfDaysFilter] = useState('all');
  const [perfLoading, setPerfLoading] = useState(false);
  
  // Custom date range picker states
  const [customDateRange, setCustomDateRange] = useState({ from: null, to: null });
  const [isCustomRange, setIsCustomRange] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  
  // Payment dialog
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentType, setPaymentType] = useState('placement');
  const [paymentNotes, setPaymentNotes] = useState('');
  
  // Edit candidate dialog
  const [editDialog, setEditDialog] = useState(false);
  const [editCandidate, setEditCandidate] = useState(null);
  const [editProcessCharge, setEditProcessCharge] = useState('');
  const [editStatus, setEditStatus] = useState('');
  
  const getAuthToken = () => localStorage.getItem('token');
  
  const fetchData = async () => {
    setLoading(true);
    const token = getAuthToken();
    const config = { headers: { Authorization: `Bearer ${token}` } };
    
    try {
      const [statsRes, partnersRes, candidatesRes, alertsRes, performanceRes] = await Promise.all([
        axios.get(`${API}/api/admin/partner-dashboard/stats`, config),
        axios.get(`${API}/api/admin/partner-dashboard/partners`, config),
        axios.get(`${API}/api/admin/partner-dashboard/candidates`, config),
        axios.get(`${API}/api/admin/partner-dashboard/alerts`, config),
        axios.get(`${API}/api/admin/partner-dashboard/monthly-performance`, config),
      ]);
      
      setStats(statsRes.data);
      setPartners(partnersRes.data);
      setCandidates(candidatesRes.data);
      setAlerts(alertsRes.data);
      setMonthlyPerformance(performanceRes.data);
    } catch (error) {
      console.error('Failed to fetch partner dashboard data:', error);
      toast.error('Failed to load partner dashboard');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchData();
  }, []);
  
  // Fetch filtered performance data
  const fetchFilteredPerformance = async () => {
    setPerfLoading(true);
    const token = getAuthToken();
    const config = { headers: { Authorization: `Bearer ${token}` } };
    
    try {
      let url = `${API}/api/admin/partner-dashboard/monthly-performance?month=${perfMonthFilter}&year=${perfYearFilter}`;
      
      if (isCustomRange && customDateRange.from && customDateRange.to) {
        // Use custom date range
        const startDate = format(customDateRange.from, 'yyyy-MM-dd');
        const endDate = format(customDateRange.to, 'yyyy-MM-dd');
        url = `${API}/api/admin/partner-dashboard/monthly-performance?start_date=${startDate}&end_date=${endDate}`;
      } else if (perfDaysFilter !== 'all') {
        url += `&days=${perfDaysFilter}`;
      }
      
      const res = await axios.get(url, config);
      setMonthlyPerformance(res.data);
    } catch (error) {
      console.error('Failed to fetch filtered performance:', error);
      toast.error('Failed to load performance data');
    } finally {
      setPerfLoading(false);
    }
  };
  
  // Reset performance filters to current month
  const resetPerformanceFilters = () => {
    const now = new Date();
    setPerfMonthFilter((now.getMonth() + 1).toString());
    setPerfYearFilter(now.getFullYear().toString());
    setPerfDaysFilter('all');
    setIsCustomRange(false);
    setCustomDateRange({ from: null, to: null });
  };
  
  // Handle custom date range selection
  const handleCustomDateSelect = (range) => {
    setCustomDateRange(range || { from: null, to: null });
    if (range?.from && range?.to) {
      setIsCustomRange(true);
      setPerfDaysFilter('all');
      setDatePickerOpen(false);
    }
  };
  
  // Effect to fetch performance data when filters change
  useEffect(() => {
    if (activeView === 'performance') {
      fetchFilteredPerformance();
    }
  }, [perfMonthFilter, perfYearFilter, perfDaysFilter, activeView, isCustomRange, customDateRange]);
  
  const handleRecordPayment = async () => {
    if (!selectedPartner || !paymentAmount) return;
    
    try {
      const token = getAuthToken();
      await axios.post(`${API}/api/admin/partner-dashboard/incentives`, {
        partner_email: selectedPartner.email,
        amount: parseInt(paymentAmount),
        payment_type: paymentType,
        notes: paymentNotes,
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success('Payment recorded successfully');
      setPaymentDialog(false);
      setPaymentAmount('');
      setPaymentNotes('');
      fetchData();
    } catch (error) {
      toast.error('Failed to record payment');
    }
  };
  
  const handleUpdateCandidate = async () => {
    if (!editCandidate) return;
    
    try {
      const token = getAuthToken();
      const updateData = {};
      
      if (editProcessCharge) {
        updateData.process_charge = parseInt(editProcessCharge);
      }
      if (editStatus) {
        updateData.status = editStatus;
      }
      
      await axios.patch(
        `${API}/api/admin/partner-dashboard/candidates/${editCandidate.id}`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Candidate updated successfully');
      setEditDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update candidate');
    }
  };
  
  const handleExport = async (format) => {
    try {
      const token = getAuthToken();
      const response = await axios.get(
        `${API}/api/admin/partner-dashboard/export?format=${format}`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: format === 'csv' ? 'blob' : 'json'
        }
      );
      
      if (format === 'csv') {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `partner_report_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } else {
        // JSON download
        const dataStr = JSON.stringify(response.data, null, 2);
        const blob = new Blob([dataStr], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `partner_report_${new Date().toISOString().split('T')[0]}.json`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      
      toast.success(`Report exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Failed to export report');
    }
  };
  
  const filteredCandidates = candidates.filter(c => {
    const matchesSearch = !searchTerm || 
      c.candidate_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.candidate_phone?.includes(searchTerm) ||
      c.referred_by_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || c.status === statusFilter;
    const matchesPartner = partnerFilter === 'all' || c.referred_by === partnerFilter;
    return matchesSearch && matchesStatus && matchesPartner;
  });
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Partner Dashboard</h2>
          <p className="text-slate-500 text-sm mt-1">Manage referral partners and track performance</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => handleExport('csv')} className="rounded-xl">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button variant="outline" onClick={() => handleExport('json')} className="rounded-xl">
            <FileText className="h-4 w-4 mr-2" />
            Export JSON
          </Button>
        </div>
      </div>
      
      {/* Navigation Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {[
          { id: 'overview', label: 'Overview', icon: BarChart3 },
          { id: 'partners', label: 'Partners', icon: Users },
          { id: 'candidates', label: 'Candidates', icon: User },
          { id: 'performance', label: 'Monthly Performance', icon: TrendingUp },
          { id: 'alerts', label: 'Alerts', icon: AlertTriangle },
        ].map(tab => (
          <Button
            key={tab.id}
            variant={activeView === tab.id ? 'default' : 'outline'}
            onClick={() => setActiveView(tab.id)}
            className={`rounded-xl whitespace-nowrap ${activeView === tab.id ? 'bg-violet-600' : ''}`}
          >
            <tab.icon className="h-4 w-4 mr-2" />
            {tab.label}
            {tab.id === 'alerts' && alerts.length > 0 && (
              <Badge className="ml-2 bg-red-500 text-white">{alerts.length}</Badge>
            )}
          </Button>
        ))}
      </div>
      
      {/* Overview View */}
      {activeView === 'overview' && stats && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-0 shadow-md rounded-xl">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Total Partners</p>
                    <p className="text-2xl font-bold text-slate-800">{stats.total_partners}</p>
                    <p className="text-xs text-emerald-600">{stats.active_partners} active</p>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-violet-100 flex items-center justify-center">
                    <Users className="h-6 w-6 text-violet-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-0 shadow-md rounded-xl">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Total Leads</p>
                    <p className="text-2xl font-bold text-slate-800">{stats.total_leads}</p>
                    <p className="text-xs text-blue-600">{stats.initial_cleared} cleared</p>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                    <TrendingUp className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-0 shadow-md rounded-xl">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Placements</p>
                    <p className="text-2xl font-bold text-slate-800">{stats.total_placements}</p>
                    <p className="text-xs text-emerald-600">Successfully hired</p>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <Award className="h-6 w-6 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-0 shadow-md rounded-xl">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Total Incentives</p>
                    <p className="text-2xl font-bold text-slate-800">₹{stats.total_incentives?.toLocaleString()}</p>
                    <p className="text-xs text-amber-600">₹{stats.pending_incentives?.toLocaleString()} pending</p>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                    <DollarSign className="h-6 w-6 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Top Performers */}
          <Card className="border-0 shadow-md rounded-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="h-5 w-5 text-amber-500" />
                Top Performers
              </CardTitle>
              <CardDescription>Partners with most placements</CardDescription>
            </CardHeader>
            <CardContent>
              {stats.top_performers?.length > 0 ? (
                <div className="space-y-3">
                  {stats.top_performers.map((performer, index) => (
                    <div key={performer.email} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                          index === 0 ? 'bg-amber-500' : index === 1 ? 'bg-slate-400' : index === 2 ? 'bg-amber-700' : 'bg-slate-300'
                        }`}>
                          {index + 1}
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">{performer.name}</p>
                          <p className="text-xs text-slate-500">{performer.email}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-emerald-600">{performer.placements} placements</p>
                        <p className="text-xs text-slate-500">{performer.referrals} total referrals</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-4">No performers yet</p>
              )}
            </CardContent>
          </Card>
          
          {/* Incentive Tiers Info */}
          <Card className="border-0 shadow-md rounded-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-emerald-500" />
                Incentive Structure
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-blue-50 rounded-xl">
                  <h4 className="font-semibold text-blue-800 mb-2">Initial Round Cleared</h4>
                  <p className="text-2xl font-bold text-blue-600">₹500</p>
                  <p className="text-sm text-blue-600">Per candidate</p>
                </div>
                <div className="p-4 bg-emerald-50 rounded-xl">
                  <h4 className="font-semibold text-emerald-800 mb-2">Placement Incentives</h4>
                  <div className="space-y-1 text-sm">
                    {PROCESS_CHARGE_OPTIONS.map(opt => (
                      <div key={opt.label} className="flex justify-between">
                        <span className="text-slate-600">{opt.label}</span>
                        <span className="font-semibold text-emerald-600">₹{opt.incentive.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Partners View */}
      {activeView === 'partners' && (
        <Card className="border-0 shadow-md rounded-xl">
          <CardHeader>
            <CardTitle>Referral Partners</CardTitle>
            <CardDescription>{partners.length} total partners</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3 text-sm font-semibold text-slate-600">Partner</th>
                    <th className="text-center p-3 text-sm font-semibold text-slate-600">Referrals</th>
                    <th className="text-center p-3 text-sm font-semibold text-slate-600">Placements</th>
                    <th className="text-right p-3 text-sm font-semibold text-slate-600">Earnings</th>
                    <th className="text-right p-3 text-sm font-semibold text-slate-600">Pending</th>
                    <th className="text-center p-3 text-sm font-semibold text-slate-600">Status</th>
                    <th className="text-center p-3 text-sm font-semibold text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {partners.map(partner => (
                    <tr key={partner.email} className="border-b hover:bg-slate-50">
                      <td className="p-3">
                        <div>
                          <p className="font-medium text-slate-800">{partner.name}</p>
                          <p className="text-xs text-slate-500">{partner.email}</p>
                          {partner.phone && <p className="text-xs text-slate-400">{partner.phone}</p>}
                        </div>
                      </td>
                      <td className="text-center p-3">
                        <span className="font-semibold">{partner.total_referrals}</span>
                        <span className="text-xs text-slate-500 block">{partner.recent_referrals} this month</span>
                      </td>
                      <td className="text-center p-3">
                        <span className="font-semibold text-emerald-600">{partner.placements}</span>
                      </td>
                      <td className="text-right p-3">
                        <span className="font-semibold">₹{partner.total_earnings?.toLocaleString()}</span>
                      </td>
                      <td className="text-right p-3">
                        <span className={`font-semibold ${partner.pending_amount > 0 ? 'text-amber-600' : 'text-slate-400'}`}>
                          ₹{partner.pending_amount?.toLocaleString()}
                        </span>
                      </td>
                      <td className="text-center p-3">
                        <div className="flex flex-col items-center gap-1">
                          <Badge className={partner.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>
                            {partner.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                          {partner.low_performer_alert && (
                            <Badge className="bg-amber-100 text-amber-700 text-xs">Low Activity</Badge>
                          )}
                        </div>
                      </td>
                      <td className="text-center p-3">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedPartner(partner);
                            setPaymentAmount(partner.pending_amount?.toString() || '');
                            setPaymentDialog(true);
                          }}
                          disabled={partner.pending_amount === 0}
                          className="rounded-lg"
                        >
                          <DollarSign className="h-4 w-4 mr-1" />
                          Pay
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Candidates View */}
      {activeView === 'candidates' && (
        <Card className="border-0 shadow-md rounded-xl">
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <CardTitle>Candidate Tracking</CardTitle>
                <CardDescription>{filteredCandidates.length} candidates</CardDescription>
              </div>
              <div className="flex flex-wrap gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search candidates..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 w-48 rounded-xl"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-36 rounded-xl">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="Pending">Pending</SelectItem>
                    <SelectItem value="Shortlisted">Initial Cleared</SelectItem>
                    <SelectItem value="Interviewed">Registered</SelectItem>
                    <SelectItem value="Hired">Placed</SelectItem>
                    <SelectItem value="Rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={partnerFilter} onValueChange={setPartnerFilter}>
                  <SelectTrigger className="w-40 rounded-xl">
                    <SelectValue placeholder="Partner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Partners</SelectItem>
                    {partners.map(p => (
                      <SelectItem key={p.email} value={p.email}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3 text-sm font-semibold text-slate-600">Candidate</th>
                    <th className="text-left p-3 text-sm font-semibold text-slate-600">Contact</th>
                    <th className="text-left p-3 text-sm font-semibold text-slate-600">Referred By</th>
                    <th className="text-left p-3 text-sm font-semibold text-slate-600">Job Role</th>
                    <th className="text-right p-3 text-sm font-semibold text-slate-600">Process Charge</th>
                    <th className="text-center p-3 text-sm font-semibold text-slate-600">Status</th>
                    <th className="text-right p-3 text-sm font-semibold text-slate-600">Incentive</th>
                    <th className="text-center p-3 text-sm font-semibold text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCandidates.map(candidate => (
                    <tr key={candidate.id} className="border-b hover:bg-slate-50">
                      <td className="p-3">
                        <p className="font-medium text-slate-800">{candidate.candidate_name}</p>
                        <p className="text-xs text-slate-500">{candidate.candidate_email}</p>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-1 text-sm text-slate-600">
                          <Phone className="h-3 w-3" />
                          {candidate.candidate_phone}
                        </div>
                      </td>
                      <td className="p-3">
                        <p className="text-sm font-medium text-slate-700">{candidate.referred_by_name}</p>
                      </td>
                      <td className="p-3">
                        <p className="text-sm text-slate-700">{candidate.job_title}</p>
                        <p className="text-xs text-slate-500">{candidate.company_name}</p>
                      </td>
                      <td className="text-right p-3">
                        <span className="font-semibold">{candidate.process_charge_display}</span>
                      </td>
                      <td className="text-center p-3">
                        <Badge className={STATUS_COLORS[candidate.status] || 'bg-slate-100'}>
                          {candidate.status === 'Shortlisted' ? 'Initial Cleared' : 
                           candidate.status === 'Interviewed' ? 'Registered' :
                           candidate.status === 'Hired' ? 'Placed' : candidate.status}
                        </Badge>
                      </td>
                      <td className="text-right p-3">
                        <span className="font-semibold text-emerald-600">₹{candidate.incentive_earned?.toLocaleString()}</span>
                        <p className="text-xs text-slate-500">{candidate.incentive_type}</p>
                      </td>
                      <td className="text-center p-3">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditCandidate(candidate);
                            setEditProcessCharge(candidate.process_charge?.toString() || '');
                            setEditStatus(candidate.status);
                            setEditDialog(true);
                          }}
                          className="rounded-lg"
                        >
                          Edit
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredCandidates.length === 0 && (
                <p className="text-center text-slate-500 py-8">No candidates found</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Monthly Performance View */}
      {activeView === 'performance' && (
        <Card className="border-0 shadow-md rounded-xl">
          <CardHeader>
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Monthly Performance</CardTitle>
                  <CardDescription>Track referrals and placements per partner</CardDescription>
                </div>
              </div>
              
              {/* Filter Controls */}
              <div className="flex flex-wrap items-center gap-3 p-3 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-slate-500" />
                  <span className="text-sm font-medium text-slate-600">Filters:</span>
                </div>
                
                {/* Month Filter */}
                <Select value={perfMonthFilter} onValueChange={setPerfMonthFilter}>
                  <SelectTrigger className="w-32 rounded-xl bg-white" data-testid="perf-month-filter">
                    <SelectValue placeholder="Month" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">January</SelectItem>
                    <SelectItem value="2">February</SelectItem>
                    <SelectItem value="3">March</SelectItem>
                    <SelectItem value="4">April</SelectItem>
                    <SelectItem value="5">May</SelectItem>
                    <SelectItem value="6">June</SelectItem>
                    <SelectItem value="7">July</SelectItem>
                    <SelectItem value="8">August</SelectItem>
                    <SelectItem value="9">September</SelectItem>
                    <SelectItem value="10">October</SelectItem>
                    <SelectItem value="11">November</SelectItem>
                    <SelectItem value="12">December</SelectItem>
                  </SelectContent>
                </Select>
                
                {/* Year Filter */}
                <Select value={perfYearFilter} onValueChange={setPerfYearFilter}>
                  <SelectTrigger className="w-28 rounded-xl bg-white" data-testid="perf-year-filter">
                    <SelectValue placeholder="Year" />
                  </SelectTrigger>
                  <SelectContent>
                    {[...Array(5)].map((_, i) => {
                      const year = new Date().getFullYear() - 2 + i;
                      return <SelectItem key={year} value={year.toString()}>{year}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
                
                {/* Days Filter */}
                <Select 
                  value={isCustomRange ? 'custom' : perfDaysFilter} 
                  onValueChange={(val) => {
                    if (val !== 'custom') {
                      setPerfDaysFilter(val);
                      setIsCustomRange(false);
                      setCustomDateRange({ from: null, to: null });
                    }
                  }}
                >
                  <SelectTrigger className="w-36 rounded-xl bg-white" data-testid="perf-days-filter">
                    <SelectValue placeholder="Time Range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Full Month</SelectItem>
                    <SelectItem value="7">Last 7 Days</SelectItem>
                    <SelectItem value="15">Last 15 Days</SelectItem>
                    <SelectItem value="30">Last 30 Days</SelectItem>
                    <SelectItem value="90">Last 90 Days</SelectItem>
                    {isCustomRange && <SelectItem value="custom">Custom Range</SelectItem>}
                  </SelectContent>
                </Select>
                
                {/* Custom Date Range Picker */}
                <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant={isCustomRange ? "default" : "outline"}
                      className={`rounded-xl ${isCustomRange ? 'bg-violet-600 text-white' : ''}`}
                      data-testid="custom-date-range-btn"
                    >
                      <CalendarRange className="h-4 w-4 mr-2" />
                      {isCustomRange && customDateRange.from && customDateRange.to
                        ? `${format(customDateRange.from, 'MMM d')} - ${format(customDateRange.to, 'MMM d')}`
                        : 'Custom Range'
                      }
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <div className="p-3 border-b">
                      <p className="text-sm font-medium text-slate-700">Select Date Range</p>
                      <p className="text-xs text-slate-500">Click start date, then end date</p>
                    </div>
                    <Calendar
                      mode="range"
                      selected={customDateRange}
                      onSelect={handleCustomDateSelect}
                      numberOfMonths={2}
                      disabled={{ after: new Date() }}
                      className="rounded-md"
                    />
                    {customDateRange.from && customDateRange.to && (
                      <div className="p-3 border-t bg-slate-50">
                        <p className="text-sm text-slate-600">
                          Selected: <span className="font-semibold">
                            {format(customDateRange.from, 'MMM d, yyyy')} - {format(customDateRange.to, 'MMM d, yyyy')}
                          </span>
                        </p>
                      </div>
                    )}
                  </PopoverContent>
                </Popover>
                
                {/* Reset Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={resetPerformanceFilters}
                  className="rounded-xl ml-auto"
                  data-testid="perf-reset-btn"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Reset
                </Button>
              </div>
              
              {/* Current Filter Display */}
              <div className="text-sm text-slate-600">
                Showing: <span className="font-semibold">
                  {isCustomRange && customDateRange.from && customDateRange.to
                    ? `${format(customDateRange.from, 'MMM d, yyyy')} - ${format(customDateRange.to, 'MMM d, yyyy')}`
                    : perfDaysFilter !== 'all' 
                      ? `Last ${perfDaysFilter} days` 
                      : `${['January','February','March','April','May','June','July','August','September','October','November','December'][parseInt(perfMonthFilter) - 1]} ${perfYearFilter}`
                  }
                </span>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {perfLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
              </div>
            ) : monthlyPerformance ? (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3 text-sm font-semibold text-slate-600">Partner</th>
                        <th className="text-center p-3 text-sm font-semibold text-slate-600">Referrals</th>
                        <th className="text-center p-3 text-sm font-semibold text-slate-600">Placements</th>
                        <th className="text-right p-3 text-sm font-semibold text-slate-600">Earnings</th>
                        <th className="text-right p-3 text-sm font-semibold text-slate-600">Bonus</th>
                        <th className="text-right p-3 text-sm font-semibold text-slate-600">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthlyPerformance.performance?.map(perf => (
                        <tr key={perf.partner_email} className="border-b hover:bg-slate-50">
                          <td className="p-3">
                            <p className="font-medium text-slate-800">{perf.partner_name}</p>
                            <p className="text-xs text-slate-500">{perf.partner_email}</p>
                          </td>
                          <td className="text-center p-3 font-semibold">{perf.referrals}</td>
                          <td className="text-center p-3">
                            <span className="font-semibold text-emerald-600">{perf.placements}</span>
                            {perf.bonus_tier && (
                              <Badge className="ml-2 bg-amber-100 text-amber-700 text-xs">{perf.bonus_tier}</Badge>
                            )}
                          </td>
                          <td className="text-right p-3">₹{perf.earnings?.toLocaleString()}</td>
                          <td className="text-right p-3">
                            <span className={perf.bonus > 0 ? 'text-amber-600 font-semibold' : 'text-slate-400'}>
                              ₹{perf.bonus?.toLocaleString()}
                            </span>
                          </td>
                          <td className="text-right p-3 font-bold text-emerald-600">
                            ₹{perf.total_with_bonus?.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {(!monthlyPerformance.performance || monthlyPerformance.performance.length === 0) && (
                    <p className="text-center text-slate-500 py-8">No performance data for selected period</p>
                  )}
                </div>
                
                {/* Bonus Tiers Info */}
                <div className="mt-6 p-4 bg-amber-50 rounded-xl">
                  <h4 className="font-semibold text-amber-800 mb-2">Monthly Bonus Tiers</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="text-center">
                      <p className="text-sm text-amber-700">5 Placements</p>
                      <p className="font-bold text-amber-800">₹2,500</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-amber-700">10 Placements</p>
                      <p className="font-bold text-amber-800">₹6,000</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-amber-700">15 Placements</p>
                      <p className="font-bold text-amber-800">₹10,000</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-amber-700">20 Placements</p>
                      <p className="font-bold text-amber-800">₹15,000</p>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <p className="text-center text-slate-500 py-8">No performance data available</p>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Alerts View */}
      {activeView === 'alerts' && (
        <Card className="border-0 shadow-md rounded-xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Partner Alerts
            </CardTitle>
            <CardDescription>{alerts.length} alerts requiring attention</CardDescription>
          </CardHeader>
          <CardContent>
            {alerts.length > 0 ? (
              <div className="space-y-3">
                {alerts.map((alert, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-xl border-l-4 ${
                      alert.severity === 'danger' 
                        ? 'bg-red-50 border-red-500' 
                        : 'bg-amber-50 border-amber-500'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          {alert.severity === 'danger' ? (
                            <AlertCircle className="h-5 w-5 text-red-500" />
                          ) : (
                            <AlertTriangle className="h-5 w-5 text-amber-500" />
                          )}
                          <span className={`font-semibold ${
                            alert.severity === 'danger' ? 'text-red-700' : 'text-amber-700'
                          }`}>
                            {alert.type === 'inactive' ? 'Inactive Partner' : 'Low Activity'}
                          </span>
                        </div>
                        <p className="font-medium text-slate-800 mt-1">{alert.partner_name}</p>
                        <p className="text-sm text-slate-600">{alert.message}</p>
                      </div>
                      <Badge className={alert.severity === 'danger' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
                        {alert.severity === 'danger' ? 'Critical' : 'Warning'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
                <p className="text-slate-600">All partners are performing well!</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Payment Dialog */}
      <Dialog open={paymentDialog} onOpenChange={setPaymentDialog}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Partner</Label>
              <p className="font-medium">{selectedPartner?.name}</p>
              <p className="text-sm text-slate-500">{selectedPartner?.email}</p>
            </div>
            <div>
              <Label>Amount (₹)</Label>
              <Input
                type="number"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                className="rounded-xl"
              />
            </div>
            <div>
              <Label>Payment Type</Label>
              <Select value={paymentType} onValueChange={setPaymentType}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="initial_cleared">Initial Cleared Incentive</SelectItem>
                  <SelectItem value="placement">Placement Incentive</SelectItem>
                  <SelectItem value="bonus">Monthly Bonus</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes</Label>
              <Input
                value={paymentNotes}
                onChange={(e) => setPaymentNotes(e.target.value)}
                placeholder="Optional notes..."
                className="rounded-xl"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentDialog(false)} className="rounded-xl">
              Cancel
            </Button>
            <Button onClick={handleRecordPayment} className="rounded-xl bg-violet-600">
              <Check className="h-4 w-4 mr-2" />
              Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit Candidate Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>Edit Candidate</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Candidate</Label>
              <p className="font-medium">{editCandidate?.candidate_name}</p>
              <p className="text-sm text-slate-500">{editCandidate?.job_title} at {editCandidate?.company_name}</p>
            </div>
            <div>
              <Label>Process Charge (₹)</Label>
              <Select value={editProcessCharge} onValueChange={setEditProcessCharge}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Select charge range" />
                </SelectTrigger>
                <SelectContent>
                  {PROCESS_CHARGE_OPTIONS.map(opt => (
                    <SelectItem key={opt.min} value={opt.min.toString()}>
                      {opt.label} → ₹{opt.incentive.toLocaleString()} incentive
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={editStatus} onValueChange={setEditStatus}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="Initial Cleared">Initial Cleared (₹500)</SelectItem>
                  <SelectItem value="Registered">Registered</SelectItem>
                  <SelectItem value="Placed">Placed</SelectItem>
                  <SelectItem value="Rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)} className="rounded-xl">
              Cancel
            </Button>
            <Button onClick={handleUpdateCandidate} className="rounded-xl bg-violet-600">
              <Check className="h-4 w-4 mr-2" />
              Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
